import { Component, OnInit, QueryList, ViewChildren, ChangeDetectionStrategy } from '@angular/core';
import { SafeHtml } from '@angular/platform-browser';
import { OpportunityUserView } from '@api-proxies';
import { AppEditorFactory } from '@factories/app-editor-factory/app-editor-factory';
import { EditorType } from '@factories/app-editor-factory/enum/editor-type';
import { OpportunityService } from '@http/clients/opportunity.service';
import { AwaiterService } from '@services/awaiter.service';
import { CDK_IMPORTS } from '@utils/standalone-imports';
import {
    EditorItemComponent,
    IconSanitizerService,
    IEditorItemConfig,
    ModalComponentBase,
    ObjectExtensionsService,
    SnackbarNotificationService,
    ValidatorType
} from 'ngx-lib';
import { LoadingButtonComponent } from '../../loading-button/loading-button.component';

interface IModalEditOpportunityUserParameters {
    user: OpportunityUserView;
}

@Component({
    selector: 'app-modal-edit-opportunity-team-member',
    templateUrl: './modal-edit-opportunity-team-member.component.html',
    styleUrls: ['./modal-edit-opportunity-team-member.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [...CDK_IMPORTS, EditorItemComponent, LoadingButtonComponent]
})
export class ModalEditOpportunityTeamMemberComponent
    extends ModalComponentBase<IModalEditOpportunityUserParameters, OpportunityUserView>
    implements OnInit
{
    protected override parameters!: IModalEditOpportunityUserParameters;
    userFullName!: string;

    teamMember!: OpportunityUserView;

    factory: AppEditorFactory;

    configItems: IEditorItemConfig[];

    @ViewChildren('editorItem')
    editors?: QueryList<EditorItemComponent>;

    userInitialState!: OpportunityUserView;

    isLoading: boolean;

    closeIcon: SafeHtml;

    constructor(
        private readonly opportunityService: OpportunityService,
        private readonly awaiter: AwaiterService,
        private readonly snackbarNotificationService: SnackbarNotificationService,
        private readonly objectExtensionService: ObjectExtensionsService,
        private readonly iconSanitizer: IconSanitizerService
    ) {
        super();
        this.factory = new AppEditorFactory();
        this.configItems = [];
        this.userFullName = '';
        this.closeIcon = this.iconSanitizer.getIcon('closeModal');
        this.isLoading = false;
    }

    ngOnInit(): void {
        this.userFullName = this.parameters?.user.userFullName ?? '';
        this.teamMember = this.objectExtensionService.clone(this.parameters?.user) as OpportunityUserView;
        this.userInitialState = this.objectExtensionService.clone(this.parameters.user) as OpportunityUserView;
        this.configItems = [
            {
                label: 'Email',
                componentId: EditorType.textInput,
                fieldName: 'email',
                bindContext: this.teamMember,
                isReadOnly: true
            },
            {
                label: 'Opportunity Roles',
                componentId: EditorType.entity,
                fieldName: 'opportunityUserUserRoles',
                entityId: 'opportunityUserRole',
                bindContext: this.teamMember,
                additionalInfo: {
                    itemDisplayKey: 'description'
                }
            },
            {
                label: 'Percentage of Commission',
                componentId: EditorType.numberInput,
                fieldName: 'commissionPercentage',
                bindContext: this.teamMember,
                validations: [
                    {
                        type: ValidatorType.number,
                        args: { maxValue: 9999, customMessage: '*Input exceeds the maximum value of 9999%' }
                    }
                ]
            }
        ];
    }

    private validate(): boolean {
        return this.editors?.map((editorItem) => editorItem.validate()).every((editorItem) => editorItem) ?? false;
    }

    async saveUser(): Promise<void> {
        if (!this.validate()) return;

        await this.awaiter.awaitAction(
            'Saving User...',
            async () => {
                const userSaved = await this.opportunityService.saveUser(this.teamMember);

                if (userSaved) {
                    this.snackbarNotificationService.success('Team member saved successfully.');
                    this.close(userSaved);
                }
            },
            (loading) => (this.isLoading = loading)
        );
    }

    onClose() {
        this.teamMember = this.userInitialState;
        this.close(this.userInitialState);
    }
}
